import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { directoryApi } from "@/core/api/directoryApi";
import { tenantWorkspaceApi } from "@/core/api/tenantWorkspaceApi";
import { useAuthStore } from "@/core/auth/authStore";
import type {
  DirectoryMembership,
  DirectoryTeam,
} from "@/core/types/directory";
import { AppIcon } from "@/shared/components/AppIcon";

interface StaffForm {
  name: string;
  username: string;
  email: string;
  mobile: string;
  jobTitle: string;
  password: string;
  teamId: string;
  roleCode: string;
  status: string;
}

const emptyStaffForm: StaffForm = {
  name: "",
  username: "",
  email: "",
  mobile: "",
  jobTitle: "کارشناس فروش",
  password: "",
  teamId: "",
  roleCode: "sales-agent",
  status: "Active",
};
const number = (value: number): string =>
  new Intl.NumberFormat("fa-IR").format(value);
const capacity = (used: number, maximum?: number): string =>
  maximum === 0
    ? `${number(used)} / نامحدود`
    : `${number(used)} / ${number(maximum ?? 0)}`;
const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("") || "پ";
const statusLabel: Record<string, string> = {
  Active: "فعال",
  Suspended: "تعلیق‌شده",
  Deactivated: "غیرفعال",
};
const statusTone: Record<string, string> = {
  Active: "bg-emerald-50 text-emerald-600",
  Suspended: "bg-amber-50 text-amber-600",
  Deactivated: "bg-rose-50 text-rose-600",
};
const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : "عملیات ناموفق بود.";

export const TeamPage = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const currentMembershipId = useAuthStore((state) => state.me?.membership.id);
  const [search, setSearch] = useState("");
  const [staffModal, setStaffModal] = useState<
    DirectoryMembership | "new" | null
  >(null);
  const [staffForm, setStaffForm] = useState<StaffForm>(emptyStaffForm);
  const [teamModal, setTeamModal] = useState<DirectoryTeam | "new" | null>(
    null,
  );
  const [teamName, setTeamName] = useState("");

  const membersQuery = useQuery({
    queryKey: ["directory", "memberships"],
    queryFn: () => directoryApi.memberships({ page: 1, pageSize: 100 }),
  });
  const activeMembersQuery = useQuery({
    queryKey: ["directory", "memberships", "active-count"],
    queryFn: () =>
      directoryApi.memberships({ status: "Active", page: 1, pageSize: 1 }),
  });
  const teamsQuery = useQuery({
    queryKey: ["directory", "teams"],
    queryFn: () => directoryApi.teams(),
  });
  const rolesQuery = useQuery({
    queryKey: ["tenant-roles"],
    queryFn: directoryApi.roles,
  });
  const subscriptionQuery = useQuery({
    queryKey: ["tenant-workspace", "subscription"],
    queryFn: tenantWorkspaceApi.subscription,
  });
  const members = membersQuery.data?.items ?? [];
  const teams = teamsQuery.data ?? [];
  const roles = (rolesQuery.data ?? []).filter(
    (role) =>
      role.isActive && (!role.isSystemRole || role.code === "sales-agent"),
  );
  const visibleMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return members.filter(
      (member) =>
        !term ||
        `${member.displayName} ${member.username} ${member.email ?? ""} ${member.teamName ?? ""}`
          .toLowerCase()
          .includes(term),
    );
  }, [members, search]);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["directory", "memberships"] }),
      queryClient.invalidateQueries({
        queryKey: ["directory", "memberships", "capacity-count"],
      }),
      queryClient.invalidateQueries({ queryKey: ["directory", "teams"] }),
    ]);
  };

  const saveStaff = useMutation({
    mutationFn: async () => {
      if (staffModal === "new") {
        const created = await tenantWorkspaceApi.createStaff({
          displayName: staffForm.name.trim(),
          username: staffForm.username.trim(),
          email: staffForm.email.trim(),
          mobile: staffForm.mobile.trim() || null,
          jobTitle: staffForm.jobTitle.trim() || null,
          initialPassword: staffForm.password,
          roleCode: staffForm.roleCode || "sales-agent",
          branchId: null,
        });
        if (staffForm.teamId)
          await directoryApi.assignMembershipTeam(
            created.membershipId,
            staffForm.teamId,
          );
        return created;
      }
      const updated = await tenantWorkspaceApi.updateStaff(
        staffModal!.membershipId,
        {
          displayName: staffForm.name.trim(),
          username: staffForm.username.trim(),
          jobTitle: staffForm.jobTitle.trim() || null,
          branchId: null,
          status: staffForm.status,
          newPassword: staffForm.password || null,
          roleCode: staffForm.roleCode || null,
        },
      );
      await directoryApi.assignMembershipTeam(
        updated.membershipId,
        staffForm.teamId || null,
      );
      return updated;
    },
    onSuccess: async () => {
      setStaffModal(null);
      setStaffForm(emptyStaffForm);
      await refresh();
    },
  });
  const saveTeam = useMutation({
    mutationFn: () =>
      teamModal === "new"
        ? directoryApi.createTeam(teamName.trim())
        : directoryApi.updateTeam(teamModal!.id, teamName.trim(), true),
    onSuccess: async () => {
      setTeamModal(null);
      setTeamName("");
      await refresh();
    },
  });
  const deleteTeam = useMutation({
    mutationFn: directoryApi.deleteTeam,
    onSuccess: refresh,
  });

  const openStaff = (member?: DirectoryMembership): void => {
    if (member)
      setStaffForm({
        name: member.displayName,
        username: member.username,
        email: member.email ?? "",
        mobile: member.mobile ?? "",
        jobTitle: member.jobTitle ?? "",
        password: "",
        teamId: member.teamId ?? "",
        roleCode: member.roleCode ?? "sales-agent",
        status: member.status,
      });
    else setStaffForm(emptyStaffForm);
    setStaffModal(member ?? "new");
  };
  const openTeam = (team?: DirectoryTeam): void => {
    setTeamName(team?.name ?? "");
    setTeamModal(team ?? "new");
  };
  const totalMemberCount = membersQuery.data?.totalCount ?? 0;
  const activeCount = activeMembersQuery.data?.totalCount ?? 0;
  const inactiveCount = Math.max(0, totalMemberCount - activeCount);
  const limitReached = Boolean(
    subscriptionQuery.data?.maxStaff &&
      totalMemberCount >= subscriptionQuery.data.maxStaff,
  );
  const staffValid =
    staffForm.name.trim() &&
    staffForm.username.trim().length >= 3 &&
    Boolean(staffForm.roleCode) &&
    (staffModal !== "new" ||
      (staffForm.email.trim() && staffForm.password.length >= 8));

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-bold text-brand-600">
            مدیریت دسترسی سازمان
          </p>
          <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">
            پرسنل و تیم‌ها
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-500">
            برای هر پرسنل حساب ورود مستقل بسازید، تیم او را تعیین کنید و دسترسی
            عملیاتی CRM را فعال نگه دارید.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <button
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-brand-200 bg-white px-4 py-3 text-sm font-black text-brand-600"
            onClick={() => openTeam()}
            type="button"
          >
            <AppIcon name="users" size={18} /> تیم جدید
          </button>
          <button
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 text-sm font-black text-white shadow-lg shadow-brand-500/20 disabled:opacity-50"
            disabled={limitReached}
            onClick={() => openStaff()}
            type="button"
          >
            <AppIcon name="plus" size={18} /> پرسنل جدید
          </button>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-500">اعضای فعال</p>
          <p className="mt-2 text-2xl font-black text-ink-950">
            {number(activeCount)}{" "}
            <span className="text-sm font-semibold text-slate-400">نفر</span>
          </p>
        </article>
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-500">غیرفعال یا تعلیق</p>
          <p className="mt-2 text-2xl font-black text-amber-500">
            {number(inactiveCount)}{" "}
            <span className="text-sm font-semibold text-slate-400">نفر</span>
          </p>
        </article>
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-500">ظرفیت واقعی پلن</p>
          <p className="mt-2 text-2xl font-black text-ink-950">
            {capacity(totalMemberCount, subscriptionQuery.data?.maxStaff)}{" "}
            <span className="text-sm font-semibold text-slate-400">کاربر</span>
          </p>
        </article>
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-500">تیم‌های فعال</p>
          <p className="mt-2 text-2xl font-black text-violet-600">
            {number(teams.length)}{" "}
            <span className="text-sm font-semibold text-slate-400">تیم</span>
          </p>
        </article>
      </section>
      {limitReached ? (
        <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-700">
          ظرفیت پرسنل این پلن تکمیل شده است. برای افزودن کاربر، لایسنس باید از
          مدیریت پلتفرم ارتقا پیدا کند.
        </div>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-black text-ink-950">فهرست پرسنل</h2>
            <p className="mt-1 text-sm text-slate-400">
              نام کاربری نمایش‌داده‌شده همراه با کد سازمانی برای ورود استفاده
              می‌شود.
            </p>
          </div>
          <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-400">
            <AppIcon name="search" size={17} />
            <input
              className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 sm:w-56"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="نام، نام کاربری یا تیم"
              value={search}
            />
          </label>
        </div>
        {membersQuery.isLoading ? (
          <div className="grid min-h-40 place-items-center text-sm font-bold text-slate-400">
            در حال دریافت پرسنل…
          </div>
        ) : membersQuery.isError ? (
          <div className="mt-5 rounded-2xl bg-rose-50 p-4 text-sm font-bold text-rose-700">
            {errorText(membersQuery.error)}
          </div>
        ) : (
          <>
            <div className="mt-5 hidden overflow-x-auto md:block">
              <table className="w-full min-w-[820px] text-right">
                <thead className="border-b border-slate-100 text-xs text-slate-400">
                  <tr>
                    <th className="px-3 py-3 font-bold">پرسنل</th>
                    <th className="px-3 py-3 font-bold">نام کاربری</th>
                    <th className="px-3 py-3 font-bold">عنوان شغلی</th>
                    <th className="px-3 py-3 font-bold">نقش</th>
                    <th className="px-3 py-3 font-bold">تیم</th>
                    <th className="px-3 py-3 font-bold">وضعیت</th>
                    <th className="px-3 py-3 font-bold">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleMembers.map((member) => (
                    <tr
                      className="border-b border-slate-50 last:border-0"
                      key={member.membershipId}
                    >
                      <td className="px-3 py-4">
                        <div className="flex items-center gap-3">
                          <span className="grid size-10 place-items-center rounded-xl bg-brand-100 text-xs font-black text-brand-600">
                            {initials(member.displayName)}
                          </span>
                          <div>
                            <p className="text-sm font-black text-slate-700">
                              {member.displayName}
                              {member.membershipId === currentMembershipId ? (
                                <span className="mr-2 text-[10px] text-brand-500">
                                  مدیر سازمان
                                </span>
                              ) : null}
                            </p>
                            <p
                              className="mt-0.5 text-xs text-slate-400"
                              dir="ltr"
                            >
                              {member.email || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <code
                          className="rounded-lg bg-slate-50 px-2 py-1 text-xs text-slate-600"
                          dir="ltr"
                        >
                          {member.username || "—"}
                        </code>
                      </td>
                      <td className="px-3 py-4 text-sm text-slate-600">
                        {member.jobTitle || "—"}
                      </td>
                      <td className="px-3 py-4 text-sm font-semibold text-violet-600">
                        {member.roleName || "بدون نقش"}
                      </td>
                      <td className="px-3 py-4 text-sm font-semibold text-slate-600">
                        {member.teamName || "بدون تیم"}
                      </td>
                      <td className="px-3 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusTone[member.status] ?? "bg-slate-100 text-slate-600"}`}
                        >
                          {statusLabel[member.status] ?? member.status}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <button
                          className="rounded-lg px-3 py-2 text-xs font-bold text-brand-600 hover:bg-brand-50 disabled:text-slate-300"
                          disabled={member.membershipId === currentMembershipId}
                          onClick={() => openStaff(member)}
                          type="button"
                        >
                          ویرایش
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 space-y-3 md:hidden">
              {visibleMembers.map((member) => (
                <article
                  className="rounded-2xl border border-slate-100 p-4"
                  key={member.membershipId}
                >
                  <div className="flex items-start gap-3">
                    <span className="grid size-10 place-items-center rounded-xl bg-brand-100 text-xs font-black text-brand-600">
                      {initials(member.displayName)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-black text-slate-700">
                        {member.displayName}
                      </p>
                      <p
                        className="mt-1 truncate text-xs text-slate-400"
                        dir="ltr"
                      >
                        {member.username} · {member.email}
                      </p>
                    </div>
                    <button
                      className="text-xs font-bold text-brand-600 disabled:text-slate-300"
                      disabled={member.membershipId === currentMembershipId}
                      onClick={() => openStaff(member)}
                      type="button"
                    >
                      ویرایش
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-600">
                      {member.jobTitle || "پرسنل"}
                    </span>
                    <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-bold text-brand-600">
                      {member.roleName || "بدون نقش"}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                      {member.teamName || "بدون تیم"}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusTone[member.status]}`}
                    >
                      {statusLabel[member.status]}
                    </span>
                  </div>
                </article>
              ))}
            </div>
            {visibleMembers.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-400">
                پرسنلی با این عبارت پیدا نشد.
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-black text-ink-950">تیم‌های سازمان</h2>
            <p className="mt-1 text-sm text-slate-400">
              پرسنل هر تیم در ثبت و پیگیری مشتریان با ساختار مشخص فعالیت
              می‌کنند.
            </p>
          </div>
          <button
            className="grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-600"
            onClick={() => openTeam()}
            type="button"
          >
            <AppIcon name="plus" size={18} />
          </button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {teams.map((team) => {
            const count = members.filter((member) =>
              member.teams.some((item) => item.id === team.id),
            ).length;
            return (
              <article
                className="rounded-2xl border border-slate-100 p-4"
                key={team.id}
              >
                <div className="flex items-start justify-between">
                  <span className="grid size-10 place-items-center rounded-xl bg-violet-50 text-violet-600">
                    <AppIcon name="users" size={19} />
                  </span>
                  <div className="flex gap-1">
                    <button
                      className="rounded-lg px-2 py-1 text-xs font-bold text-slate-500 hover:bg-slate-50"
                      onClick={() => openTeam(team)}
                      type="button"
                    >
                      ویرایش
                    </button>
                    <button
                      className="rounded-lg px-2 py-1 text-xs font-bold text-rose-500 hover:bg-rose-50"
                      onClick={() => {
                        if (
                          window.confirm(
                            `تیم «${team.name}» حذف شود؟ اعضا بدون تیم خواهند شد.`,
                          )
                        )
                          deleteTeam.mutate(team.id);
                      }}
                      type="button"
                    >
                      حذف
                    </button>
                  </div>
                </div>
                <h3 className="mt-4 font-black text-slate-800">{team.name}</h3>
                <p className="mt-1 text-xs text-slate-400">
                  {number(count)} عضو
                </p>
              </article>
            );
          })}
          {teams.length === 0 ? (
            <button
              className="rounded-2xl border border-dashed border-slate-200 p-8 text-sm font-bold text-brand-600 sm:col-span-2 xl:col-span-3"
              onClick={() => openTeam()}
              type="button"
            >
              اولین تیم سازمان را ایجاد کنید
            </button>
          ) : null}
        </div>
      </section>

      {staffModal ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/40 sm:place-items-center sm:p-4">
          <button
            aria-label="بستن"
            className="absolute inset-0"
            onClick={() => setStaffModal(null)}
            type="button"
          />
          <section className="relative z-10 max-h-[94vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-2xl sm:rounded-3xl sm:p-7">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-black text-ink-950">
                  {staffModal === "new"
                    ? "ساخت حساب پرسنل"
                    : "ویرایش حساب پرسنل"}
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  کاربر با کد سازمانی، نام کاربری و رمز عبور زیر وارد CRM
                  می‌شود.
                </p>
              </div>
              <button
                className="grid size-9 place-items-center rounded-xl bg-slate-50 text-slate-500"
                onClick={() => setStaffModal(null)}
                type="button"
              >
                <AppIcon name="close" size={18} />
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field label="نام و نام خانوادگی">
                <input
                  className="input"
                  onChange={(event) =>
                    setStaffForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  value={staffForm.name}
                />
              </Field>
              <Field label="نام کاربری انگلیسی">
                <input
                  className="input"
                  dir="ltr"
                  onChange={(event) =>
                    setStaffForm((current) => ({
                      ...current,
                      username: event.target.value,
                    }))
                  }
                  placeholder="sara.ahmadi"
                  value={staffForm.username}
                />
              </Field>
              {staffModal === "new" ? (
                <>
                  <Field label="ایمیل کاری">
                    <input
                      className="input"
                      dir="ltr"
                      onChange={(event) =>
                        setStaffForm((current) => ({
                          ...current,
                          email: event.target.value,
                        }))
                      }
                      type="email"
                      value={staffForm.email}
                    />
                  </Field>
                  <Field label="شماره همراه">
                    <input
                      className="input"
                      dir="ltr"
                      onChange={(event) =>
                        setStaffForm((current) => ({
                          ...current,
                          mobile: event.target.value,
                        }))
                      }
                      value={staffForm.mobile}
                    />
                  </Field>
                </>
              ) : (
                <Field label="ایمیل">
                  <input
                    className="input disabled:bg-slate-50"
                    disabled
                    dir="ltr"
                    value={staffForm.email}
                  />
                </Field>
              )}
              <Field label="عنوان شغلی">
                <input
                  className="input"
                  onChange={(event) =>
                    setStaffForm((current) => ({
                      ...current,
                      jobTitle: event.target.value,
                    }))
                  }
                  value={staffForm.jobTitle}
                />
              </Field>
              <Field label="نقش و سطح دسترسی">
                <select
                  className="input bg-white"
                  onChange={(event) =>
                    setStaffForm((current) => ({
                      ...current,
                      roleCode: event.target.value,
                    }))
                  }
                  value={staffForm.roleCode}
                >
                  <option value="">نقش را انتخاب کنید</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.code}>
                      {role.name}
                      {role.isSystemRole ? " (سیستمی)" : ""}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-400">
                  دسترسی این پرسنل بر اساس نقش انتخاب‌شده تعیین می‌شود.
                </p>
              </Field>
              <Field label="تیم">
                <select
                  className="input bg-white"
                  onChange={(event) =>
                    setStaffForm((current) => ({
                      ...current,
                      teamId: event.target.value,
                    }))
                  }
                  value={staffForm.teamId}
                >
                  <option value="">بدون تیم</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </Field>
              {staffModal !== "new" ? (
                <Field label="وضعیت">
                  <select
                    className="input bg-white"
                    onChange={(event) =>
                      setStaffForm((current) => ({
                        ...current,
                        status: event.target.value,
                      }))
                    }
                    value={staffForm.status}
                  >
                    <option value="Active">فعال</option>
                    <option value="Suspended">تعلیق‌شده</option>
                    <option value="Deactivated">غیرفعال</option>
                  </select>
                </Field>
              ) : null}
              <Field
                label={
                  staffModal === "new"
                    ? "رمز عبور اولیه (حداقل ۸ نویسه)"
                    : "رمز عبور جدید (اختیاری)"
                }
              >
                <input
                  className="input"
                  dir="ltr"
                  onChange={(event) =>
                    setStaffForm((current) => ({
                      ...current,
                      password: event.target.value,
                    }))
                  }
                  type="password"
                  value={staffForm.password}
                />
              </Field>
            </div>
            {saveStaff.isError ? (
              <div className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">
                {errorText(saveStaff.error)}
              </div>
            ) : null}
            <div className="mt-7 flex gap-3">
              <button
                className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600"
                onClick={() => setStaffModal(null)}
                type="button"
              >
                انصراف
              </button>
              <button
                className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-black text-white disabled:opacity-50"
                disabled={saveStaff.isPending || !staffValid}
                onClick={() => saveStaff.mutate()}
                type="button"
              >
                {saveStaff.isPending
                  ? "در حال ذخیره…"
                  : staffModal === "new"
                    ? "ساخت حساب"
                    : "ذخیره تغییرات"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {teamModal ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/40 sm:place-items-center sm:p-4">
          <button
            aria-label="بستن"
            className="absolute inset-0"
            onClick={() => setTeamModal(null)}
            type="button"
          />
          <section className="relative z-10 w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-md sm:rounded-3xl sm:p-7">
            <h2 className="text-xl font-black text-ink-950">
              {teamModal === "new" ? "ایجاد تیم جدید" : "ویرایش تیم"}
            </h2>
            <label className="mt-6 block">
              <span className="mb-2 block text-xs font-bold text-slate-500">
                نام تیم
              </span>
              <input
                autoFocus
                className="input"
                onChange={(event) => setTeamName(event.target.value)}
                placeholder="مثلاً فروش تهران"
                value={teamName}
              />
            </label>
            {saveTeam.isError ? (
              <p className="mt-3 text-xs font-bold text-rose-600">
                {errorText(saveTeam.error)}
              </p>
            ) : null}
            <div className="mt-7 flex gap-3">
              <button
                className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600"
                onClick={() => setTeamModal(null)}
                type="button"
              >
                انصراف
              </button>
              <button
                className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-black text-white disabled:opacity-50"
                disabled={saveTeam.isPending || !teamName.trim()}
                onClick={() => saveTeam.mutate()}
                type="button"
              >
                ذخیره تیم
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement => (
  <label>
    <span className="mb-2 block text-xs font-bold text-slate-500">{label}</span>
    {children}
  </label>
);
